import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../components/feature/SEOHead';
import IGHero from './components/IGHero';
import IGProblema from './components/IGProblema';
import IGExercicios from './components/IGExercicios';
import IGDepoimentos from './components/IGDepoimentos';
import IGFAQ from './components/IGFAQ';
import OfertaPlanos from '../links/components/OfertaPlanos';
import ExitIntentLinks from '../links/components/ExitIntentLinks';
import {
  META_PIXEL_ID,
  initMetaPixel,
  trackPageView,
  trackViewContent,
  trackLead,
  trackInitiateCheckout,
} from '../../lib/metaPixel';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const instagramSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'PNL Gratuita para Executivas | Bia Holmes',
    url: `${SITE_URL}/instagram`,
    inLanguage: 'pt-BR',
    description: '12 exercícios gratuitos de PNL para executivas. Descubra qual padrão mental está travando seus resultados e receba o exercício exato para desbloqueá-lo agora.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Instagram', item: `${SITE_URL}/instagram` },
      ],
    },
    author: {
      '@type': 'Person',
      name: 'Bia Holmes',
      url: `${SITE_URL}/sobre`,
      jobTitle: 'Especialista em PNL Clínica e Alta Performance',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Exercícios de PNL Gratuitos — Bia Holmes',
    url: `${SITE_URL}/instagram`,
    description: 'Programa de reprogramação mental com 12 exercícios gratuitos de PNL para executivas e mulheres líderes.',
    provider: {
      '@type': 'Person',
      name: 'Bia Holmes',
      url: `${SITE_URL}/sobre`,
    },
    areaServed: {
      '@type': 'Country',
      name: 'Brasil',
    },
    isAccessibleForFree: true,
    inLanguage: 'pt-BR',
    serviceType: 'Programação Neurolinguística',
  },
];

// --- hook: pixel init -------------------------------------------------
function useInstagramPixel() {
  useEffect(() => {
    initMetaPixel(META_PIXEL_ID);
    trackPageView();
  }, []);
}

// --- Navbar -----------------------------------------------------------
function IGNavbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-luxury-black/95 backdrop-blur-md border-b border-white/[0.06]' : 'bg-transparent'
    }`}>
      <div className="flex items-center justify-between px-5 py-4 max-w-lg mx-auto">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gold-500/15 border border-gold-500/30">
            <i className="ri-sparkles-line text-gold-400 text-sm" />
          </div>
          <span className="font-display text-sm font-semibold text-white/80 tracking-wide">Bia Holmes</span>
        </Link>
        <Link
          to="/diagnostico"
          className="flex items-center gap-1.5 px-4 py-2 bg-gold-500/15 border border-gold-500/30 rounded-full text-gold-400 text-xs font-bold tracking-wide hover:bg-gold-500/25 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-brain-line text-xs" />
          Diagnóstico Grátis
        </Link>
      </div>
    </header>
  );
}

// --- Oferta com ViewContent tracking ----------------------------------
function IGOferta() {
  const sectionRef = useRef<HTMLElement>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            trackViewContent();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 px-5 bg-gradient-to-b from-luxury-black via-[#0f0e0b] to-luxury-black"
    >
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/25 rounded-full mb-5">
            <i className="ri-instagram-line text-[#E1306C] text-sm" />
            <span className="text-[11px] font-bold text-gold-400 tracking-[0.18em] uppercase">Oferta exclusiva Instagram</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">
            Pronta para ir além<br />
            <span className="text-gold-400">dos exercícios gratuitos?</span>
          </h2>
          <p className="text-white/45 text-sm leading-relaxed max-w-sm mx-auto">
            Acesso completo ao método — com áudios guiados, workbooks e suporte direto. Com desconto exclusivo para quem veio do Instagram hoje.
          </p>
        </div>

        <OfertaPlanos onCheckoutClick={() => trackInitiateCheckout()} />

        <div className="mt-5 text-center">
          <p className="text-xs text-white/25 mb-2">Prefere começar sem compromisso?</p>
          <Link
            to="/exercicios-gratis"
            className="inline-flex items-center gap-1.5 text-xs text-gold-400/60 hover:text-gold-400 transition-colors font-medium cursor-pointer"
          >
            <i className="ri-sparkling-2-line text-xs" />
            Ver os 12 exercícios gratuitos
            <i className="ri-arrow-right-line text-xs" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// --- CTA Final com Lead tracking ------------------------------------
function IGCTAFinal() {
  const navigate = useNavigate();

  const handlePlanosCTA = () => {
    trackLead({ content_name: 'CTA Final Instagram' });
    navigate('/planos');
  };

  return (
    <section className="py-20 px-5 bg-gradient-to-br from-[#1a1610] via-charcoal-900 to-[#0d0c09] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(201,164,64,0.08)_0%,_transparent_65%)] pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />

      <div className="relative max-w-lg mx-auto text-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-gold-500/12 border border-gold-500/25 mx-auto mb-6">
          <i className="ri-double-quotes-l text-2xl text-gold-400/60" />
        </div>

        <blockquote className="font-display text-xl sm:text-2xl font-semibold text-white leading-snug mb-4">
          &ldquo;Os exercícios gratuitos foram tão transformadores que no mesmo dia assinei o plano completo. Não me arrependo nenhum segundo.&rdquo;
        </blockquote>
        <p className="text-gold-400/70 text-sm font-medium mb-10">
          — Ana Paula M., São Paulo · Diretora de Operações
        </p>

        <div className="flex flex-col gap-3 max-w-xs mx-auto">
          <button
            type="button"
            onClick={handlePlanosCTA}
            className="group flex items-center justify-center gap-2.5 w-full py-4 rounded-2xl bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-700 hover:to-gold-600 text-charcoal-900 font-bold text-base tracking-wide transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-vip-crown-line text-lg" />
            Garantir minha vaga agora
          </button>
          <Link
            to="/diagnostico"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl border border-white/12 bg-white/[0.03] hover:bg-white/[0.07] text-white/60 hover:text-white font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-brain-line text-gold-400 text-base" />
            Começar pelo diagnóstico grátis
          </Link>
        </div>

        <div className="flex items-center justify-center gap-4 mt-8 flex-wrap">
          {[
            { icon: 'ri-shield-check-line', text: 'Garantia 7 dias' },
            { icon: 'ri-lock-line', text: 'Pagamento seguro' },
            { icon: 'ri-infinity-line', text: 'Acesso vitalício' },
          ].map((g, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[11px] text-white/25">
              <i className={`${g.icon} text-white/20`} />
              {g.text}
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/[0.06]">
          <p className="text-[10px] text-white/15 mb-3">© {new Date().getFullYear()} Bia Holmes · Todos os direitos reservados</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/privacidade" className="text-[10px] text-white/15 hover:text-white/35 transition-colors">Privacidade</Link>
            <span className="text-white/10">·</span>
            <Link to="/termos" className="text-[10px] text-white/15 hover:text-white/35 transition-colors">Termos</Link>
            <span className="text-white/10">·</span>
            <Link to="/" className="text-[10px] text-white/15 hover:text-white/35 transition-colors">Site principal</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Page -------------------------------------------------------------
export default function InstagramLandingPage() {
  useInstagramPixel();

  /* Dispara Lead quando qualquer CTA da Hero é clicado */
  const handleHeroCTA = () => {
    trackLead({ content_name: 'Hero CTA Instagram' });
  };

  return (
    <div className="min-h-screen bg-luxury-black" onClick={(e) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href="/planos"]');
      if (link) trackLead({ content_name: 'Link /planos Instagram' });
    }}>
      <SEOHead
        title="PNL Gratuita para Executivas | Bia Holmes — Reprogramação Mental"
        description="12 exercícios gratuitos de PNL para executivas. Descubra qual padrão mental está travando seus resultados e receba o exercício exato para desbloqueá-lo agora."
        keywords="PNL gratuita, exercícios PNL, reprogramação mental, Bia Holmes, alta performance feminina"
        canonical="/instagram"
        ogType="website"
        schema={instagramSchema}
      />

      {/* Meta Pixel noscript fallback */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>

      <IGNavbar />
      <IGHero onCtaClick={handleHeroCTA} />
      <IGProblema />
      <IGExercicios />
      <IGDepoimentos />
      <IGOferta />
      <IGFAQ />
      <IGCTAFinal />
      <ExitIntentLinks />
    </div>
  );
}
